<script setup lang="ts">
import LeTooltip from "@dsk/components/LeTooltip.vue";
import { uniq } from "lodash-es";
import { computed } from "vue";

import type { DictsysArticle, DictsysWordclass } from "@/types";

const props = defineProps<{
  wordclass: DictsysWordclass;
  usageLanguage: DictsysArticle["usageLanguage"];
}>();

const isGerman = computed<boolean>(() => props.usageLanguage == "de");

const wordclassShort = computed<string>(() => {
  if (isGerman.value) {
    return props.wordclass.germanShort;
  }

  return props.wordclass.danishShort;
});

const wordclassLong = computed<string>(() => {
  if (isGerman.value) {
    return props.wordclass.german;
  }

  return props.wordclass.danish;
});

const tooltipText = computed<string>(() => {
  if (isGerman.value && props.wordclass.german) {
    return props.wordclass.german;
  }

  const parts = [];

  if (props.wordclass.danishLatin) {
    parts.push(props.wordclass.danishLatin);
  }

  if (props.wordclass.danish) {
    parts.push(props.wordclass.danish);
  }

  return uniq(parts).join(" / ");
});

const hasTooltip = computed<boolean>(() => {
  if (!props.wordclass.key) {
    return false;
  }

  if (wordclassShort.value === tooltipText.value) {
    return false;
  }

  return true;
});
</script>

<template>
  <span v-if="wordclassShort">
    <LeTooltip v-if="hasTooltip" :text="tooltipText" :margin-bottom="8" class="le-article-wordclass" aria-hidden="true">
      {{ wordclassShort }}
    </LeTooltip>
    <span v-else class="le-article-wordclass">
      {{ wordclassShort }}
    </span>

    <div class="sr-only">{{ wordclassLong }}</div>
  </span>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";
@import "@/assets/styles/dictsys-article";

.le-article-wordclass {
  color: @colors[@neutrals][300];
  .dictsys-article-text();
}
</style>
